import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaEdit, FaChartBar, FaClone, FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { budgetService } from '../../../services/apiService';

const BudgetDetail = () => {
  const { id } = useParams();
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBudget = async () => {
      try {
        setLoading(true);
        const data = await budgetService.getBudgetById(id);
        setBudget(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching budget:', err);
        setError('Failed to load budget');
        toast.error('Failed to load budget details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBudget();
    }
  }, [id]);

  if (loading) {
    return <div className="flex justify-center p-8">Loading budget details...</div>;
  }

  if (error || !budget) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          <p>{error || 'Budget not found'}</p>
          <Link to="/budgeting" className="text-blue-500 hover:underline mt-2 inline-block">
            Back to Budgets
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const handleClone = async () => {
    try {
      await budgetService.cloneBudget(id);
      toast.success('Budget cloned successfully');
    } catch (error) {
      console.error('Error cloning budget:', error);
      toast.error('Failed to clone budget');
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center mb-6">
        <Link to="/budgeting" className="text-blue-500 hover:text-blue-700 mr-4">
          <FaArrowLeft className="inline mr-1" /> Back to Budgets
        </Link>
        <h1 className="text-2xl font-semibold text-gray-800">{budget.name}</h1>
      </div>

      <div className="mb-4 text-gray-600">
        <p>Period: {formatDate(budget.period.startDate)} - {formatDate(budget.period.endDate)}</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-blue-700">Total Income</h3>
          <p className="text-2xl font-bold text-gray-800">{formatCurrency(budget.totalPlannedIncome)}</p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-red-700">Total Expenses</h3>
          <p className="text-2xl font-bold text-gray-800">{formatCurrency(budget.totalPlannedExpenses)}</p>
        </div>
        <div className={`p-4 rounded-lg ${budget.totalPlannedIncome - budget.totalPlannedExpenses >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
          <h3 className="text-lg font-medium text-gray-800">Balance</h3>
          <p className={`text-2xl font-bold ${budget.totalPlannedIncome - budget.totalPlannedExpenses >= 0 ? 'text-green-700' : 'text-red-700'}`}>
            {formatCurrency(budget.totalPlannedIncome - budget.totalPlannedExpenses)}
          </p>
        </div>
      </div>

      {/* Income Section */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-semibold text-gray-800">Income Sources</h2>
        </div>
        {budget.income.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left text-gray-700 font-semibold">Source</th>
                  <th className="p-3 text-right text-gray-700 font-semibold">Planned Amount</th>
                  <th className="p-3 text-right text-gray-700 font-semibold">Actual Amount</th>
                  <th className="p-3 text-right text-gray-700 font-semibold">Difference</th>
                </tr>
              </thead>
              <tbody>
                {budget.income.map((source, index) => (
                  <tr key={source._id || index} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-gray-800">{source.name}</td>
                    <td className="p-3 text-right text-gray-800">{formatCurrency(source.plannedAmount)}</td>
                    <td className="p-3 text-right text-gray-800">
                      {formatCurrency(source.actualAmount || 0)}
                    </td>
                    <td className="p-3 text-right">
                      <span className={
                        (source.actualAmount || 0) >= source.plannedAmount 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }>
                        {formatCurrency((source.actualAmount || 0) - source.plannedAmount)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 p-4 text-center rounded-lg">
            <p className="text-gray-500">No income sources added to this budget.</p>
          </div>
        )}
      </div>

      {/* Expense Categories Section */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-semibold text-gray-800">Expense Categories</h2>
        </div>
        {budget.categories.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left text-gray-700 font-semibold">Category</th>
                  <th className="p-3 text-right text-gray-700 font-semibold">Planned Amount</th>
                  <th className="p-3 text-right text-gray-700 font-semibold">Actual Amount</th>
                  <th className="p-3 text-right text-gray-700 font-semibold">Difference</th>
                  <th className="p-3 text-center text-gray-700 font-semibold">% of Budget</th>
                </tr>
              </thead>
              <tbody>
                {budget.categories.map((category, index) => (
                  <tr key={category._id || index} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-gray-800">{category.name}</td>
                    <td className="p-3 text-right text-gray-800">{formatCurrency(category.plannedAmount)}</td>
                    <td className="p-3 text-right text-gray-800">{formatCurrency(category.actualAmount || 0)}</td>
                    <td className="p-3 text-right">
                      <span 
                        className={
                          category.plannedAmount >= (category.actualAmount || 0) 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }
                      >
                        {formatCurrency(category.plannedAmount - (category.actualAmount || 0))}
                      </span>
                    </td>
                    <td className="p-3 text-center text-gray-800">
                      {budget.totalPlannedExpenses > 0 
                        ? `${((category.plannedAmount / budget.totalPlannedExpenses) * 100).toFixed(1)}%` 
                        : '0%'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 p-4 text-center rounded-lg">
            <p className="text-gray-500">No expense categories added to this budget.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetDetail; 